import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useSmoothScroll } from "@/lib/smooth";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Brands from "@/pages/Brands";
import Blog from "@/pages/Blog";
import ArticleDetail from "@/pages/ArticleDetail";
import Team from "@/pages/Team";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import NotFound from "@/pages/NotFound";
import { SERVICES, PROJECTS, slugify } from "@/data/site";
import { getArticle } from "@/data/blog";

const META = {
  "/": { t: "Air Conditioning & Mechanical Services Melbourne | J&S Air Conditioning", d: "Melbourne air conditioning & mechanical services contractor since 1988. VRV/VRF, ducted & split systems, mechanical ventilation & car park exhaust for residential, commercial, industrial & educational projects." },
  "/about": { t: "About J&S Air Conditioning | Melbourne HVAC Specialists", d: "J&S Air Conditioning is an experienced Melbourne mechanical services contractor delivering quality air conditioning and ventilation for builders, developers and project teams." },
  "/services": { t: "J&S Air Conditioning | Air Conditioning & Mechanical Services Melbourne", d: "Air conditioning installation, VRV/VRF, ducted & split systems, mechanical ventilation and car park exhaust systems across Melbourne and Victoria." },
  "/projects": { t: "J&S Air Conditioning | HVAC Projects Melbourne", d: "Completed and current air conditioning and mechanical services projects across residential, commercial, industrial and educational developments in Melbourne." },
  "/brands": { t: "Air Conditioning Brands — Daikin, Mitsubishi Electric & More | J&S", d: "Authorised installers of premium air conditioning brands including Daikin, Mitsubishi Electric, Hisense, Panasonic and ActronAir across Melbourne." },
  "/blog": { t: "News & Insights | J&S Air Conditioning Melbourne", d: "Guides and insights on air conditioning, heating, ventilation and energy efficiency for Melbourne homes and businesses." },
  "/team": { t: "Our Team | J&S Air Conditioning Melbourne", d: "Meet the licensed, experienced team behind J&S Air Conditioning, Melbourne's mechanical services contractor." },
  "/careers": { t: "Careers | J&S Air Conditioning Melbourne", d: "Join J&S Air Conditioning — HVAC and mechanical services careers in Melbourne. View current openings and apply online." },
  "/contact": { t: "Contact J&S Air Conditioning | Melbourne", d: "Contact J&S Air Conditioning in Tullamarine, Melbourne. Request a project quote for air conditioning and mechanical services across Victoria." },
  "/quote": { t: "Request a Quote | J&S Air Conditioning Melbourne", d: "Request a free, no-obligation quote for air conditioning and mechanical services in Melbourne. Residential, commercial, industrial and educational projects." },
};

const ORIGIN = "https://jandsairconditioning.com.au";

const CRUMB_LABEL = {
  "/about": "About", "/services": "Services", "/projects": "Projects",
  "/brands": "Brands", "/blog": "Blog", "/team": "Team",
  "/careers": "Careers", "/contact": "Contact", "/quote": "Request a Quote",
};

const canonicalFor = (pathname) => ORIGIN + (pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));

const buildBreadcrumb = (pathname) => {
  if (pathname === "/") return null;
  const home = { name: "Home", path: "/" };
  if (pathname.startsWith("/services/")) {
    const s = SERVICES.find((x) => x.slug === pathname.split("/")[2]);
    return [home, { name: "Services", path: "/services" }, { name: s ? s.name : "Service", path: pathname }];
  }
  if (pathname.startsWith("/projects/")) {
    const p = PROJECTS.find((x) => slugify(x.title) === pathname.split("/")[2]);
    return [home, { name: "Projects", path: "/projects" }, { name: p ? p.title : "Project", path: pathname }];
  }
  if (pathname.startsWith("/blog/")) {
    const a = getArticle(pathname.split("/")[2]);
    return [home, { name: "Blog", path: "/blog" }, { name: a ? a.title : "Article", path: pathname }];
  }
  const label = CRUMB_LABEL[pathname];
  return label ? [home, { name: label, path: pathname }] : null;
};

const setMeta = (rawPath) => {
  const pathname = rawPath !== "/" ? rawPath.replace(/\/+$/, "") : "/";
  let title, desc;
  let notFound = false;
  if (META[pathname]) { ({ t: title, d: desc } = META[pathname]); }
  else if (pathname.startsWith("/services/")) {
    const s = SERVICES.find((x) => x.slug === pathname.split("/")[2]);
    if (s) { title = `${s.name} Melbourne | J&S Air Conditioning`; desc = s.overview; }
    else { notFound = true; }
  } else if (pathname.startsWith("/projects/")) {
    const p = PROJECTS.find((x) => slugify(x.title) === pathname.split("/")[2]);
    if (p) { title = `${p.title} — ${p.type} | J&S Air Conditioning Melbourne`; desc = (p.description || "").slice(0, 155); }
    else { notFound = true; }
  } else if (pathname.startsWith("/blog/")) {
    const a = getArticle(pathname.split("/")[2]);
    if (a) { title = a.seoTitle; desc = a.metaDescription; }
    else { notFound = true; }
  } else { notFound = true; }

  if (notFound) { title = "Page Not Found | J&S Air Conditioning Melbourne"; desc = ""; }

  document.title = title;
  if (desc) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", desc);
  }

  // robots meta: noindex only for unknown/not-found routes
  let robots = document.querySelector('meta[name="robots"]');
  if (notFound) {
    if (!robots) { robots = document.createElement("meta"); robots.setAttribute("name", "robots"); document.head.appendChild(robots); }
    robots.setAttribute("content", "noindex, follow");
  } else if (robots) {
    robots.remove();
  }

  // Self-referencing canonical (omit for not-found pages)
  let link = document.querySelector('link[rel="canonical"]');
  if (notFound) {
    if (link) link.remove();
  } else {
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonicalFor(pathname));
  }

  // Open Graph title + url per route
  const setProp = (prop, val) => {
    let el = document.querySelector(`meta[property="${prop}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
    el.setAttribute("content", val);
  };
  setProp("og:title", title);
  setProp("og:url", notFound ? ORIGIN + "/" : canonicalFor(pathname));

  // Breadcrumb structured data (skip homepage & not-found)
  const prev = document.getElementById("breadcrumb-jsonld");
  if (prev) prev.remove();
  const crumbs = notFound ? null : buildBreadcrumb(pathname);
  if (crumbs) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "breadcrumb-jsonld";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem", position: i + 1, name: c.name, item: ORIGIN + (c.path === "/" ? "/" : c.path),
      })),
    });
    document.head.appendChild(s);
  }
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    setMeta(pathname);
  }, [pathname]);
  return null;
};

function App() {
  useSmoothScroll();
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/brands" element={<Brands />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<ArticleDetail />} />
            <Route path="/team" element={<Team />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <ChatWidget />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
